import { functionsJson } from "@corca/data";

/**
 * Anything with a node's `type` / `props` pair: node JSON, or an editor node model. The web
 * app's `semanticChecks` wraps the operator checks to narrow a model to its model class.
 * The guards narrow to `Maybe<…>`, so a false answer still leaves an operator of some other type.
 */
type NodeShape = { type: string; props?: object };

type Maybe<T> = T & { readonly _brand?: never };

const DIFFERENTIAL_KEYS: string[] = functionsJson
  .filter((func) => func.isDifferential)
  .map((func) => func.key);

function readProp(node: NodeShape, key: string): unknown {
  return (node.props as Record<string, unknown> | undefined)?.[key];
}

/** Whether a node is a differential function (`d`, `∂`, …). */
export function isDifferential<T extends NodeShape>(
  node?: T | null,
): node is Maybe<Extract<T, { type: "function" }>> {
  const key = node?.type === "function" ? readProp(node, "key") : undefined;
  return typeof key === "string" && DIFFERENTIAL_KEYS.includes(key);
}

export function isPowerOperator<T extends NodeShape>(
  node?: T | null,
): node is Maybe<Extract<T, { type: "operator" }>> {
  return node?.type === "operator" && readProp(node, "operatorType") === "power";
}

export function isFraction<T extends NodeShape>(
  node?: T | null,
): node is Maybe<Extract<T, { type: "operator" }>> {
  return node?.type === "operator" && readProp(node, "operatorType") === "fraction";
}
